import React from "react";
import ReactApexChart from "react-apexcharts";
import SkeletonLoader from "../Loaders/SkeletonLoader";

interface VerticalGraphProps {
  data: { date: string; overall_performance: number }[];
  loading?: boolean;
  formatDateLabel: (date: Date, index: number, allDates: Date[]) => string;
}

const VerticalGraph: React.FC<VerticalGraphProps> = ({ data, loading = false, formatDateLabel }) => {
  const [chartKey, setChartKey] = React.useState(0);

  React.useEffect(() => {
    const observer = new MutationObserver(() => {
      setChartKey((prev) => prev + 1);
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["style"] });
    return () => observer.disconnect();
  }, []);

  const textColor = getComputedStyle(document.documentElement).getPropertyValue("--text-color").trim();
  const primaryColor = getComputedStyle(document.documentElement).getPropertyValue("--svg-color").trim();

  if (loading) return <SkeletonLoader type="horizontal-chart" />;

  const dateObjects = data.map((d) => new Date(d.date));
  const categories = dateObjects.map((date, index) =>
    formatDateLabel(date, index, dateObjects)
  );

  // CHANGE 1: Use actual values instead of capping them at 100
  const seriesData = data.map((d) => d.overall_performance);

  // CHANGE 2: Calculate dynamic max. 
  // If max value is < 100, default to 100. If > 100, use the actual max + small buffer.
  const rawMax = Math.max(...seriesData);
  const maxValue = rawMax > 100 ? Math.ceil(rawMax * 1.1) : 100;

  const options: ApexCharts.ApexOptions = {
    chart: {
      type: "bar",
      height: 250,
      foreColor: textColor,
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 5,
        barHeight: "60%",
      },
    },
    xaxis: {
      categories: categories,
      min: 0,
      max: maxValue, // CHANGE 3: Apply dynamic max here
      tickAmount: 5,
      labels: {
        style: { colors: textColor },
        formatter: (val) => `${Number(val).toFixed(0)}%`, // Clean up decimals
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: textColor,
          fontSize: "12px",
        },
      },
    },
    fill: {
      colors: [primaryColor],
      opacity: 1,
    },
    dataLabels: { enabled: false },
    tooltip: {
      theme: "dark",
      y: {
        formatter: (val) => `${val}%`,
      },
    },
    grid: {
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: false } },
    },
  };

  const series = [{ name: "Performance", data: seriesData }];

  return (
    <>
      <ReactApexChart key={chartKey} options={options} series={series} type="bar" height={250} />
    </>
  );
};

export default VerticalGraph;